import { useEffect, useRef, useState } from "react";
import { Camera, CameraOff, LogIn, LogOut, ScanLine } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState, Field, PageHeader, StatusBadge } from "@/components/shared/Common";
import { EmployeeAvatar } from "@/components/shared/EmployeeAvatar";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtTime } from "@/lib/format";
import { Pick } from "@/pages/gerencia/Sales";

export const ENAB = { habilitado: ["activo", "Habilitado"], observado: ["pendiente", "Observado"], habilitado_excepcion: ["info", "Habilitado por excepción"],
  no_habilitado: ["bloqueado", "No habilitado"], no_asignado: ["bloqueado", "No asignado a esta OT"], inactivo: ["inactivo", "Trabajador inactivo"] };
const RESULT = { entrada_registrada: ["activo", "Entrada registrada"], salida_registrada: ["activo", "Salida registrada"], ya_registrado: ["info", "Ya estaba registrada"],
  rechazado: ["bloqueado", "No se registró: no habilitado"], periodo_cerrado: ["bloqueado", "Período de asistencia cerrado"], sin_entrada: ["bloqueado", "No tiene entrada registrada hoy"] };
const OK = ["entrada_registrada", "salida_registrada"];

export const ModeButtons = ({ kind, setKind }) => (
  <div className="grid grid-cols-2 gap-3" data-testid="scan-mode">
    {[["entrada", "ENTRADA", "AM", LogIn], ["salida", "SALIDA", "PM", LogOut]].map(([k, l, s, Icon]) => (
      <button key={k} type="button" data-testid={`scan-mode-${k}`} onClick={() => setKind(k)}
        className={`flex items-center justify-center gap-3 rounded-2xl border-2 py-4 text-lg font-extrabold transition-transform active:scale-95 ${kind === k ? (k === "entrada" ? "bg-[#258F00] border-[#258F00] text-white" : "bg-[#17210f] border-[#17210f] text-white") : "bg-white border-[#e4e8e1] text-[#5b6656] hover:border-[#258F00]"}`}>
        <Icon size={22} />{l}<span className="rounded-full bg-white/20 px-2 text-xs font-mono">{s}</span>
      </button>))}
  </div>
);

export const CameraReader = ({ onRead }) => {
  const ref = useRef(null);
  const last = useRef({ t: "", at: 0 });
  const cb = useRef(onRead);
  cb.current = onRead;
  useEffect(() => {
    const qr = new Html5Qrcode("qr-camera");
    ref.current = qr;
    const started = qr.start({ facingMode: "environment" }, { fps: 8, qrbox: 240 }, (text) => {
      if (text === last.current.t && Date.now() - last.current.at < 4000) return;
      last.current = { t: text, at: Date.now() };
      cb.current(text);
    }).catch(() => toast.error("No fue posible abrir la cámara"));
    return () => { started.then(() => qr.isScanning && qr.stop()).catch(() => {}); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <div id="qr-camera" data-testid="qr-camera" className="w-full max-w-sm overflow-hidden rounded-xl border border-[#e4e8e1]" />;
};

const ResultCard = ({ r }) => (
  <div data-testid="scan-result" className={`syc-card p-5 flex flex-col sm:flex-row gap-5 items-start syc-rise border-2 ${OK.includes(r.result) ? "border-[#5CD500]" : ["rechazado", "sin_entrada", "periodo_cerrado"].includes(r.result) ? "border-[#f5c2c0]" : "border-[#e4e8e1]"}`}>
    <EmployeeAvatar id={r.employee.id} v={r.employee.photo_v} name={r.employee.name} size={110} testid="scan-result-photo" />
    <div className="flex-1 min-w-0 space-y-2">
      <div className="text-2xl font-extrabold" data-testid="scan-result-name">{r.employee.name}</div>
      <div className="text-sm text-[#5b6656]">RUT {r.employee.rut} · {r.employee.internal_id} · {r.employee.position}</div>
      <div className="flex flex-wrap gap-2"><StatusBadge testid="scan-result-enablement" status={ENAB[r.enablement][0]} label={`${r.ot.code}: ${ENAB[r.enablement][1]}`} />
        <StatusBadge testid="scan-result-attendance" status={RESULT[r.result][0]} label={OK.includes(r.result) || r.result === "ya_registrado" ? `${RESULT[r.result][1]} · ${r.kind === "entrada" ? "Entrada" : "Salida"} ${fmtTime(r.ts)} · ${fmtDate(r.date)}` : RESULT[r.result][1]} /></div>
      {r.other_ot && <div className="text-xs text-[#b42318]">Ya registró entrada hoy en otra OT</div>}
    </div>
  </div>
);

export default function Scan() {
  const [ots, setOts] = useState([]);
  const [codes, setCodes] = useState([]);
  const [ot, setOt] = useState("");
  const [code, setCode] = useState("FAE");
  const [text, setText] = useState("");
  const [cam, setCam] = useState(false);
  const [log, setLog] = useState([]);
  const [kind, setKind] = useState(new Date().getHours() < 12 ? "entrada" : "salida");
  const input = useRef(null);
  useEffect(() => {
    api.get("/rrhh/control-doc/ots").then((r) => setOts(r.data)).catch(() => {});
    api.get("/rrhh/attendance-codes").then((r) => setCodes(r.data.filter((c) => c.active).map((c) => ({ id: c.code, name: `${c.code} · ${c.name}` }))));
  }, []);
  const send = async (qr) => {
    if (!ot) return toast.error("Seleccione la OT");
    try { const { data } = await api.post("/rrhh/scan", { qr, ot_id: ot, code, kind }); setLog((l) => [data, ...l].slice(0, 20)); OK.includes(data.result) ? toast.success(`${data.employee.name}: ${RESULT[data.result][1]}`) : toast.warning(`${data.employee.name}: ${RESULT[data.result][1]}`); }
    catch (e) { toast.error(errMsg(e)); }
    setText(""); input.current?.focus();
  };
  return (
    <div data-testid="scan-page">
      <PageHeader eyebrow="RR.HH. · Terreno" title="Escanear carnet" subtitle="Presione ENTRADA (AM) o SALIDA (PM) y escanee el QR del reverso de la cédula con un lector USB o con la cámara. La entrada exige estar habilitado en la OT y marca la asistencia del día." />
      <div className="syc-card p-5 mb-5 space-y-4">
        <ModeButtons kind={kind} setKind={(k) => { setKind(k); input.current?.focus(); }} />
        <div className="grid sm:grid-cols-[1fr_220px] gap-4">
          <Field label="OT *"><Pick testid="scan-ot-select" value={ot} onChange={(v) => { setOt(v); setTimeout(() => input.current?.focus(), 50); }} options={ots} placeholder="Seleccione la OT" /></Field>
          <Field label="Código a registrar"><Pick testid="scan-code-select" value={code} onChange={setCode} options={codes} /></Field>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); text.trim() && send(text.trim()); }} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1"><ScanLine size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#258F00]" />
            <Input ref={input} autoFocus data-testid="scan-input" className="pl-9" placeholder="Lector QR: escanee aquí (o escriba el RUT y presione Enter)" value={text} onChange={(e) => setText(e.target.value)} /></div>
          <Button data-testid="scan-submit-btn" className="rounded-full">Verificar</Button>
          <Button type="button" data-testid="scan-camera-btn" variant="outline" className="rounded-full" onClick={() => setCam(!cam)}>{cam ? <><CameraOff size={15} className="mr-1" />Cerrar cámara</> : <><Camera size={15} className="mr-1" />Usar cámara</>}</Button>
        </form>
        {cam && <CameraReader onRead={send} />}
      </div>
      <div className="space-y-3">{log.length === 0 ? <div className="syc-card"><EmptyState title="Sin escaneos en esta sesión" /></div> : log.map((r, i) => <ResultCard key={i} r={r} />)}</div>
    </div>
  );
}
